import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  pdf,
} from '@react-pdf/renderer';
import React from 'react';
import { Button } from 'reactstrap';
import { API_URL, baseUrl, getAgreementDocument } from '../../api';
import StyledButton from '../../components/Common/StyledButton';
import { formatDate } from '../../utility/formatDate';
import getPaymentTerm from '../../utility/getPaymentTerm';

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    paddingVertical: 10,
  },
  section: {
    margin: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 4,
  },
  addressSection: {
    margin: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 12,
  },
  address: {
    width: '49%',
  },
  table: {
    display: 'table',
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCol: {
    width: '10%',
    flex: 1,
    borderStyle: 'solid',
    borderWidth: 1,
    padding: 5,
    borderColor: 'gray'
  },
  pageBorder: {
    position: 'absolute',
    top: 30,
    right: 30,
    bottom: 30,
    left: 30,
    borderWidth: 1,
    borderColor: 'gray'
  },
  firstCol: {
    width: '6%',
    borderStyle: 'solid',
    borderWidth: 1,
    padding: 5,
    borderColor: 'gray'
  },
  titleCol: {
    width: '60%',
    borderStyle: 'solid',
    borderWidth: 1,
    padding: 5,
    borderColor: 'gray'
  },
  tableCell: {
    fontSize: 8,
    textAlign: 'center',
  },
  header: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  info: {
    fontSize: 9,
  },
  divider: {
    borderBottomWidth: 0.5,
    borderBottomColor: 'black',
    marginVertical: 10,
  },
  logoImage: {
    height: 60,
    width: 100,
  },
  amtCard: {
    width: '40%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 3,
    backgroundColor: '#58db83',
    borderRadius: '10px',
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  amtText: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
  },
});

// Function to display table with pagination
const TableWithPagination = ({ data }) => {
  return (
    <View>
      {/* Table Header */}
      <View style={styles.tableRow} fixed>
        <View style={[styles.firstCol, { borderRight: 0, backgroundColor: '#D3D3D3' }]}>
          <Text style={[styles.tableCell, styles.header]}>#</Text>
        </View>
        <View style={[styles.titleCol, { borderRight: 0, backgroundColor: '#D3D3D3' }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Item Name</Text>
        </View>
        <View style={[styles.tableCol, { borderRight: 0, backgroundColor: '#D3D3D3' }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Unit</Text>
        </View>

        <View style={[styles.tableCol, { borderRight: 0, backgroundColor: '#D3D3D3' }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Tax</Text>
        </View>
        <View style={[styles.tableCol, { backgroundColor: '#D3D3D3' }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>
            Price
          </Text>
        </View>

      </View>

      {/* Table Rows */}
      {data.map((item, index) => (
        <View style={styles.tableRow} key={index} wrap={false}>
          <View style={[styles.firstCol, { borderRight: 0, borderTop: 0 }]}>
            <Text style={styles.tableCell}>{index + 1}</Text>
          </View>
          <View style={[styles.titleCol, { borderRight: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'left' }]}>
              {item?.alternateItemTitle}
            </Text>
          </View>
          <View style={[styles.tableCol, { borderRight: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'left' }]}>
              {item.unit}
            </Text>
          </View>
          <View style={[styles.tableCol, { borderRight: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'right' }]}>
              {`${(item.tax?.match(/\d+(\.\d+)?/g) || [])
                .reduce((sum, num) => sum + parseFloat(num), 0)} %`}
            </Text>
          </View>
          <View style={[styles.tableCol, { borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'right' }]}>
              {item.sellingPrice}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
};

// Define the PDF document structure
const PdfDocument = ({ data }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View fixed style={styles.pageBorder} />
        <View style={styles.section}>
          <View style={[styles.address, { width: '25% !important' }]}>
            <Image
              src={require('../../assets/images/Willsmeet-Logo.png')}
              style={styles.logoImage}
            />
          </View>
          <View style={[styles.address, { width: '40% !important' }]}>
            <Text style={styles.header}>
              Bansi Office Solutions Private Limited
            </Text>
            <Text style={styles.info}>
              #1496, 19th Main Road, Opp Park Square Apartment, HSR Layout,
              Bangalore Karnataka 560102, India
            </Text>
            <Text style={styles.info}>
              Web: www.willsmeet.com, Email:sales@willsmeet.com
            </Text>
          </View>
        </View>

        <View>
          <Text style={[styles.title, { textAlign: 'center' }]}>
            AGREEMENT DETAILS
          </Text>
        </View>

        <View style={[styles.section, { paddingVertical: 10 }]}>
          <View>
            <Text style={[styles.info, { paddingVertical: 4, fontWeight: 'bold' }]}>
              <Text>Name:</Text>{data.name}
            </Text>
            <Text style={[styles.info, { paddingVertical: 4 }]}>
              <Text style={{ fontWeight: 'bold' }}>Email:</Text> {data.email}
            </Text>
            <Text style={[styles.info, { paddingVertical: 4 }]}>
              <Text style={{ fontWeight: 'bold' }}>Contact:</Text> {data.contact}
            </Text>
            <Text style={[styles.info, { paddingVertical: 4 }]}>
              <Text style={{ fontWeight: 'bold' }}>Agreement Validity:</Text> {formatDate(data.validity)}
            </Text>
            <Text style={[styles.info, { paddingTop: 4 }]}>
              <Text style={{ fontWeight: 'bold' }}>Payment Terms:</Text> {getPaymentTerm(data.paymentTerms)}
            </Text>
            <Text style={[styles.info, { paddingTop: 4 }]}>
                <Text style={{ fontWeight: 'bold' }}>Version: </Text>
                {!data?.isDeleted
                  ? 'Current Version'
                  : `V${data?.version}`}
              </Text>
            </View>
        </View>

        <View style={styles.table}>
          <View style={{ borderWidth: 1, borderColor: 'gray', borderBottom: 0, borderTop: 0 }}>
            <Text style={[styles.header, { paddingBottom: 10, marginLeft: 10 }]}>
              Agreement Items
            </Text>
          </View>

          <TableWithPagination data={data.displayTableData} />
        </View>
        <Text
          fixed
          style={{
            position: 'absolute',
            bottom: 15,
            left: 0,
            right: 30,
            fontSize: 8,
            textAlign: 'right',
          }}
          render={({ pageNumber,totalPages }) =>
            totalPages>1?`${pageNumber}`:''
          }
        />
      </Page>
    </Document>
  );
};

const AgreementPdfComponent = ({ data }) => {

  const handleOpenPdf = async () => {
    const blob = await pdf(<PdfDocument data={data} />).toBlob();
    const url = URL.createObjectURL(blob);

    window.open(url, '_blank');

  };

  return (
    <>
      {/* <PDFDownloadLink document={<PdfDocument data={data} />} fileName={`${data.purchaseOrderNumber}.pdf`}>
        {({ blob, url, loading, error }) =>
          <Button color="primary" outline onClick={handleOpenPdf}>{loading ? 'Loading...' : 'Download PDF'}</Button>
        }
      </PDFDownloadLink> */}
      {data.page === 'client' ? (
        <div className="d-flex flex-column gap-2">
          <Button color="primary" size="sm" onClick={handleOpenPdf}>
            <i className="mdi mdi-download mx-2"></i>
            Download PDF
          </Button>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          <StyledButton
            color={'primary'}
            onClick={handleOpenPdf}
            type="button"
            className={'w-md agreement-btn'}
          >
            <i className={'btn-icon mdi mdi-download'}></i>
            Download Agreement
          </StyledButton>
        </div>
      )}
    </>
  );
};

export default AgreementPdfComponent;
